import { useState, useEffect, useMemo } from "react";
import { FaDollarSign } from "react-icons/fa";
import moment from "moment";

const DisbursementsManager = () => {
  const [latestDisbursement, setLatestDisbursement] = useState("");
  const [targetDate, setTargetDate] = useState();
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const targetDates = useMemo(() => {
    if (!latestDisbursement) return [];

    const [monthStr, yearStr] = latestDisbursement.split("-");
    const startMonth = parseInt(monthStr, 10) - 1; // 0-based for Date
    const startYear = parseInt(yearStr, 10);

    const options = [];

    for (let i = 0; i < 5; i++) {
      const current = new Date(startYear, startMonth - i, 1); // e.g. Aug 1
      const future = new Date(startYear, startMonth - i + 1, 1); // e.g. Sep 1

      const label = current.toLocaleString("default", {
        month: "short",
        year: "numeric",
      }); // e.g. "Aug 2025"

      const currentYear = current.getFullYear();
      const currentMonth = (current.getMonth() + 1).toString().padStart(2, "0");

      const futureYear = future.getFullYear();
      const futureMonth = (future.getMonth() + 1).toString().padStart(2, "0");

      options.push({
        label,
        currentDate:
          i === 0 ? "" : `${currentYear}-${currentMonth}-02T00:00:00`,
        futureDate: i === 0 ? "" : `${futureYear}-${futureMonth}-02T00:00:00`,
      });
    }

    return options;
  }, [latestDisbursement]);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_PREPAYMENTS_URL}/disbursements/latest`)
      .then((res) => res.ok && res.text())
      .then((res) => res && setLatestDisbursement(res))
      .catch((err) => err);
  }, []);

  useEffect(() => {
    if (targetDates.length > 0 && !targetDate) {
      setTargetDate(targetDates[0]);
    }
  }, [targetDates, targetDate]);

  // These functions call microservice endpoints to perform large operations
  // This is an alternative to these services being automated
  async function postRunDisbursements() {
    const requestOptions = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ targetDate: targetDate.futureDate }),
    };
    const response = await fetch(
      `${process.env.REACT_APP_PREPAYMENTS_URL}/disbursements/run`,
      requestOptions
    );
    return await response.text();
  }

  async function downloadDisbursements() {
    if (!downloading) {
      setDownloading(true);
      const requestOptions = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ targetDate: targetDate.currentDate }),
      };
      let filename = "statements.zip";

      await fetch(
        `${process.env.REACT_APP_PREPAYMENTS_URL}/disbursements/download`,
        requestOptions
      )
        .then((response) => {
          if (!response.ok) {
            return response.text().then((errorMessage) => {
              throw new Error(errorMessage || response.statusText);
            });
          }
          const header = response.headers.get("Content-Disposition");
          const parts = header.split(";");
          filename = parts[1].split("=")[1].replace(/['"]+/g, "");
          return response.blob();
        })
        .then((blob) => {
          const url = window.URL.createObjectURL(new Blob([blob]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", filename);
          document.body.appendChild(link);
          link.click();
          link.parentNode.removeChild(link);
          setDownloading(false);
        })
        .catch((error) => {
          alert(`Something went wrong: ${error.message}`);
          setDownloading(false);
        });
    }
  }

  return (
    <div>
      <div className="ml-2">
        <p className="text-gray-500">
          Last disbursement:{" "}
          {latestDisbursement
            ? moment(latestDisbursement, "MM-YYYY").format("MMMM YYYY")
            : "Loading..."}
        </p>
      </div>

      <div className="ml-2 mt-5">
        <div
          className={`flex flex-row w-fit bg-primary brandPrimaryColor py-2.5 px-4 rounded${
            !latestDisbursement ? " opacity-50 pointer-events-none" : ""
          }`}
        >
          <label className="mr-2 my-auto">Disbursement Month:</label>
          <select
            className="border rounded"
            disabled={!latestDisbursement}
            name="target-date"
            value={targetDate?.currentDate}
            onChange={(e) => {
              const targetDateObj = targetDates.find(
                (x) => x.currentDate === e.target.value
              );
              setTargetDate(targetDateObj);
            }}
          >
            {targetDates.map((x) => (
              <option key={x.label} value={x.currentDate} label={x.label} />
            ))}
          </select>
        </div>
      </div>

      <div className="ml-2 mt-5">
        <button
          className={`flex flex-row border border-black bg-primary py-2 px-4 rounded brandPrimaryColor align-center${
            !latestDisbursement ? " opacity-50 pointer-events-none" : ""
          }`}
          disabled={!latestDisbursement}
          onClick={async () => {
            if (!loading) {
              setLoading(true);
              try {
                var res = await postRunDisbursements();
                alert(res);
              } catch (err) {
                alert(err?.message);
              }
              setLoading(false);
            }
          }}
        >
          {loading ? (
            <p className="mr-1 text-white inline my-auto">...</p>
          ) : (
            <FaDollarSign className="text-white inline mr-1 my-auto" />
          )}
          <p className="mt-0.5">Run Disbursements</p>
        </button>
      </div>
      <div className="ml-2 mt-5">
        <button
          className={`flex flex-row border border-black bg-primary py-2 px-4 rounded brandPrimaryColor align-center${
            !latestDisbursement ? " opacity-50 pointer-events-none" : ""
          }`}
          disabled={!latestDisbursement}
          onClick={downloadDisbursements}
        >
          {downloading ? (
            <p className="mr-1 text-white inline my-auto">...</p>
          ) : (
            <FaDollarSign className="text-white inline mr-1 my-auto" />
          )}
          <p className="mt-0.5">Download Disbursements</p>
        </button>
      </div>
    </div>
  );
};

export default DisbursementsManager;
